/**
 * BprGraphSection - the engagement's "Knowledge graph" tab: builds (or
 * reuses) the cached knowledge-graph version over the engagement's ingested
 * transcripts, then renders the two D1 views off the same graph - the
 * system-architecture map and the prioritized inverted triangle.
 *
 * The build POST is long-running (one thinking-tier model call); the cache
 * short-circuit makes repeat builds instant when the transcript set is
 * unchanged, and Rebuild forces a fresh version row.
 */

import type { BprGraphVersionDetail } from '@protoss/shared';
import { Button, Pill } from '@protoss/ui';
import * as React from 'react';
import { bprApi, type BprGraphDiagramResult } from '../services/bprApi';
import { BprGraphPriorityView } from './BprGraphPriorityView';
import { BprGraphSystemView } from './BprGraphSystemView';

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export interface BprGraphSectionProps {
  engagementUuid: string;
}

export function BprGraphSection({ engagementUuid }: BprGraphSectionProps) {
  const [version, setVersion] = React.useState<BprGraphVersionDetail | null>(null);
  const [diagram, setDiagram] = React.useState<BprGraphDiagramResult | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isBuilding, setIsBuilding] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [latest, latestDiagram] = await Promise.all([
        bprApi.getLatestGraph(engagementUuid),
        bprApi.getGraphDiagram(engagementUuid),
      ]);
      setVersion(latest);
      setDiagram(latestDiagram);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the knowledge graph.');
    } finally {
      setIsLoading(false);
    }
  }, [engagementUuid]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const handleBuild = async (force: boolean) => {
    setIsBuilding(true);
    setError(null);
    try {
      await bprApi.buildGraph(engagementUuid, force);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Graph build failed.');
    } finally {
      setIsBuilding(false);
    }
  };

  if (isLoading) {
    return <p className="bpr-section__empty">Loading knowledge graph…</p>;
  }

  if (!version || !diagram) {
    return (
      <div className="bpr-section__empty bpr-graph__unbuilt">
        <p>
          No knowledge graph built yet. Upload session transcripts, then build the graph to
          extract findings and open questions into the inverted triangle.
        </p>
        {error ? <p className="bpr-dialog__error">{error}</p> : null}
        <Button type="button" size="sm" disabled={isBuilding} onClick={() => void handleBuild(false)}>
          {isBuilding ? 'Building… (about a minute)' : 'Build graph'}
        </Button>
      </div>
    );
  }

  const stats = version.stats;

  return (
    <div className="bpr-graph">
      <div className="bpr-section__toolbar">
        <h2 className="bpr-section__heading">Knowledge graph</h2>
        <span className="bpr-graph__meta">
          {stats ? (
            <>
              <Pill variant="neutral">{stats.findings} findings</Pill>
              <Pill variant="neutral">{stats.questions} questions</Pill>
              <Pill variant="ghost">
                {stats.nodes} nodes / {stats.edges} edges
              </Pill>
            </>
          ) : null}
          <span className="bpr-graph__built-at">built {formatDateTime(version.generatedAt)}</span>
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={isBuilding}
          onClick={() => void handleBuild(true)}
        >
          {isBuilding ? 'Rebuilding…' : 'Rebuild'}
        </Button>
      </div>
      {error ? <p className="bpr-dialog__error">{error}</p> : null}

      <section className="bpr-graph__panel">
        <h3 className="bpr-section__subheading">System architecture</h3>
        <p className="bpr-graph__hint">
          Layer 1 of the triangle: the systems the client runs and the integration points between
          them. Dashed red edges are integration gaps; badges count the findings located on a
          system.
        </p>
        <BprGraphSystemView systemView={diagram.system_view} exportTitle="System map" />
      </section>

      <section className="bpr-graph__panel">
        <h3 className="bpr-section__subheading">Prioritized findings</h3>
        <p className="bpr-graph__hint">
          Deterministic tiers: explicit client escalations and deprioritizations override the
          weighted score (dependency centrality, go-live and audit risk, open decisions). Effort
          only sequences within a tier.
        </p>
        <BprGraphPriorityView priorityView={diagram.priority_view} />
      </section>
    </div>
  );
}
